// src/lib/caution-mode.js — ADR-174 : SOURCE UNIQUE du mode de caution.
//
// ⚠️ UN SEUL FICHIER pour le serveur ET l'écran (F10 phase 2, 03/10). Écrit en
// CommonJS pur : l'écran (CRA) l'importe directement ; le serveur le lit par le
// relais `lib/caution-mode.js` (`module.exports = require(...)`), comme
// src/lib/sms-validator.js. Plus aucune copie à tenir en phase : ne rien
// ajouter qui ne soit chargeable des deux côtés (garde :
// src/lib/doublons-lib-conformite.test.js, « sources uniques »).
//
// ⚠️ CHEMIN ARGENT. Ce module concentre l'UNIQUE règle métier qui décide, selon
// la durée du séjour, si la caution est prise en EMPREINTE (capture_method
// Stripe `manual`, jamais débitée — un simple hold de 7 j) ou en DÉBIT RÉEL
// (capture_method `automatic`, prélevée puis remboursée après le séjour).
//
// Pourquoi une source unique (ADR-174)
// ─────────────────────────────────────
// Le seuil `CAUTION_HOLD_MAX_NIGHTS` et la règle empreinte/débit vivaient en
// dur dans api/stripe.js (décision Stripe autoritative). Les TEXTES envoyés au
// voyageur (SMS/e-mail/page paiement) doivent dire EXACTEMENT ce que Stripe
// fait — sinon LITIGE (« vous m'aviez dit empreinte, vous avez débité »). Pour
// garantir que le wording et la décision ne divergent JAMAIS, les deux dérivent
// désormais de CE module :
//   - api/stripe.js importe `CAUTION_HOLD_MAX_NIGHTS` (décision Stripe, chemin
//     argent — comparaison `nbNuits > CAUTION_HOLD_MAX_NIGHTS` INCHANGÉE) ;
//   - les variables de gabarit {caution_empreinte}/{caution_debit} (les deux
//     jumeaux template-variables) dérivent via `cautionModeForNights`.
// Le mode est REFLÉTÉ par les textes, JAMAIS piloté par eux : le module ne
// contient aucun effet de bord, aucune décision Stripe — juste le seuil et une
// fonction pure.
//
// Règle métier
// ────────────
//   nbNuits ≤ CAUTION_HOLD_MAX_NIGHTS  → 'empreinte' (hold `manual`, jamais débité)
//   nbNuits >  CAUTION_HOLD_MAX_NIGHTS  → 'debit'     (débit réel `automatic`)
// Le seuil garde une marge pour l'état des lieux avant expiration du hold Stripe
// (lien caution J-2 → hold ~J+5) ; au-delà, aucune empreinte ne couvre tout le
// séjour → débit réel + remboursement post-séjour.
//
// Surchargeable par env (règle GLOBALE, pas par logement). Sur le serveur
// (Node : `process` toujours défini), l'expression vaut STRICTEMENT
// `Number(process.env.CAUTION_HOLD_MAX_NIGHTS) || 4` — l'ancienne constante
// d'api/stripe.js — BORNÉE depuis le 30/10 par le maximum sûr (plus bas). La protection `typeof process` vient
// de l'ancienne copie écran (la plus stricte des deux) : dans le bundle CRA,
// la variable n'existe pas (REACT_APP_* uniquement) → repli 4, aligné sur le
// défaut serveur.

// ── SEUIL RÉGLABLE PAR LOGEMENT (décisions de Dimitri du 30/10, n° 17) ──────
//
// LA RÈGLE DE REFUS, écrite UNE fois (serveur, écran, opérateur la lisent ici) :
// une empreinte posée par le voyageur lui-même, en ligne, n'est gardée par
// Stripe que 7 jours (doc officielle Stripe ; 4 j 18 h chez Visa quand ce
// n'est PAS le voyageur qui paie — jamais notre cas : la page de caution est
// toujours payée par lui). Elle doit couvrir : l'ouverture 2 jours avant
// l'arrivée (src/lib/caution-window.js), toutes les nuits, et 1 jour de
// constat après le départ. 2 + nuits + 1 ≤ 7 → 4 nuits au plus.
// Ce maximum est CALCULÉ depuis ces trois nombres, jamais tapé à la main.
const { JOURNEY_CAUTION_WINDOW_DAYS } = require("./caution-window.js");

const STRIPE_EMPREINTE_JOURS = 7;
const CAUTION_JOURS_CONSTAT = 1;
const CAUTION_SEUIL_MAX_SUR =
  STRIPE_EMPREINTE_JOURS - JOURNEY_CAUTION_WINDOW_DAYS - CAUTION_JOURS_CONSTAT;

// Relecture R4 de #1738 : le seuil commun est BORNÉ par le maximum sûr. Une
// variable CAUTION_HOLD_MAX_NIGHTS réglée au-delà (6, 10…) poserait une
// empreinte que Stripe lâche avant le constat : elle est ramenée à 4. Sans
// variable, ou à 4 et moins, la valeur est celle d'avant.
const CAUTION_HOLD_MAX_NIGHTS = Math.min(
  Number(
    typeof process !== "undefined" && process.env
      ? process.env.CAUTION_HOLD_MAX_NIGHTS
      : undefined,
  ) || 4,
  CAUTION_SEUIL_MAX_SUR,
);

function _nuits(n) {
  return `${n} nuit${n > 1 ? "s" : ""}`;
}

/**
 * La phrase qui explique le refus d'un seuil trop long (une seule source :
 * l'écran l'affiche telle quelle, le serveur la renvoie telle quelle).
 */
const PHRASE_REFUS_SEUIL =
  `Au-delà de ${_nuits(CAUTION_SEUIL_MAX_SUR)}, l’empreinte expirerait avant le départ : ` +
  `Stripe ne la garde que ${STRIPE_EMPREINTE_JOURS} jours (ouverte ${JOURNEY_CAUTION_WINDOW_DAYS} jours ` +
  `avant l’arrivée, plus ${CAUTION_JOURS_CONSTAT} jour de constat après le départ). ` +
  `Choisissez ${_nuits(CAUTION_SEUIL_MAX_SUR)} au plus ; au-delà, la caution est prélevée puis remboursée.`;

const PHRASE_SEUIL_ILLISIBLE = "Indiquez le seuil en nuits (1 au moins).";

/**
 * Refus d'un seuil d'empreinte saisi (texte ou nombre).
 * @returns {string|null} la phrase de refus, ou null si le seuil est accepté.
 */
function refusSeuilEmpreinte(brut) {
  const t = typeof brut === "number" ? String(brut) : String(brut ?? "").trim();
  if (!/^\d+$/.test(t) || Number(t) < 1) return PHRASE_SEUIL_ILLISIBLE;
  if (Number(t) > CAUTION_SEUIL_MAX_SUR) return PHRASE_REFUS_SEUIL;
  return null;
}

function _seuilRangeLisible(v) {
  return (
    typeof v === "number" &&
    Number.isInteger(v) &&
    v >= 1 &&
    v <= CAUTION_SEUIL_MAX_SUR
  );
}

/**
 * Seuil empreinte / prélèvement d'un logement (fonction PURE) : le réglage
 * `caution_seuil_empreinte_nuits` de la chambre, sinon celui du logement
 * parent, sinon le seuil commun CAUTION_HOLD_MAX_NIGHTS (le comportement
 * d'avant, à l'identique). Une valeur rangée hors de 1..maximum sûr (saisie
 * à la main) est ignorée comme une valeur absente : on ne pose JAMAIS une
 * empreinte plus longue que ce que Stripe garde.
 *
 * @param {object|null} logFromCfg — log aplati (flattenLogs, peut porter parentLog)
 * @returns {number}
 */
function seuilEmpreintePourLog(logFromCfg) {
  for (const noeud of [logFromCfg, logFromCfg?.parentLog]) {
    const v = noeud?.caution_seuil_empreinte_nuits;
    if (_seuilRangeLisible(v)) return v;
  }
  return CAUTION_HOLD_MAX_NIGHTS;
}

/**
 * Mode de caution pour une durée de séjour donnée (fonction PURE).
 *
 * Fail-safe : toute valeur non strictement > seuil (0, NaN, undefined, dates
 * manquantes en amont) retombe sur 'empreinte' — le mode le PLUS PRUDENT
 * juridiquement (« empreinte, jamais débitée » n'est faux que si l'on prélève)
 * ET conforme au fallback pessimiste d'api/stripe.js (capture_method 'manual'
 * quand nbNuits === 0 : « jamais de débit réel sur data pourrie »).
 *
 * @param {number} nbNuits nombre de nuits (end_date − start_date)
 * @returns {'empreinte'|'debit'}
 */
function cautionModeForNights(nbNuits) {
  return nbNuits > CAUTION_HOLD_MAX_NIGHTS ? "debit" : "empreinte";
}

/**
 * Mode de caution d'UN logement pour un séjour (la règle unique avec seuil
 * réglable) : même fail-safe que cautionModeForNights (non strictement >
 * seuil → 'empreinte'). Sans réglage, identique à cautionModeForNights.
 *
 * @param {object|null} logFromCfg
 * @param {number} nbNuits
 * @returns {'empreinte'|'debit'}
 */
function cautionModePourLog(logFromCfg, nbNuits) {
  return nbNuits > seuilEmpreintePourLog(logFromCfg) ? "debit" : "empreinte";
}

module.exports = {
  CAUTION_HOLD_MAX_NIGHTS,
  cautionModeForNights,
  STRIPE_EMPREINTE_JOURS,
  CAUTION_JOURS_CONSTAT,
  CAUTION_SEUIL_MAX_SUR,
  PHRASE_REFUS_SEUIL,
  PHRASE_SEUIL_ILLISIBLE,
  refusSeuilEmpreinte,
  seuilEmpreintePourLog,
  cautionModePourLog,
};
